'use client';

import { useEffect, useRef } from 'react';

interface KeyboardDismissProviderProps {
	children: React.ReactNode;
}

function useKeyboardDismiss() {
	const lastScrollY = useRef(0);
	const scrollThreshold = 10; // Minimum scroll distance to trigger keyboard dismissal
	const isUserTouchScrolling = useRef(false);
	const touchStartY = useRef(0);
	const scrollTimeout = useRef<NodeJS.Timeout | undefined>(undefined);

	useEffect(() => {
		// Only run on iOS devices (iPad, iPhone, iPod)
		const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
		if (!isIOS) return;

		const handleTouchStart = (e: TouchEvent) => {
			if (e.touches.length === 1) {
				touchStartY.current = e.touches[0].clientY;
				isUserTouchScrolling.current = false;
			}
		};

		const handleTouchMove = (e: TouchEvent) => {
			if (e.touches.length === 1) {
				const touchMoveY = e.touches[0].clientY;
				const touchDelta = Math.abs(touchMoveY - touchStartY.current);

				// Mark as user-initiated scrolling if touch moved significantly
				if (touchDelta > scrollThreshold) {
					isUserTouchScrolling.current = true;
				}
			}
		};

		const handleTouchEnd = () => {
			// Reset touch scrolling flag after a delay
			setTimeout(() => {
				isUserTouchScrolling.current = false;
			}, 100);
		};

		const handleScroll = () => {
			const currentScrollY = window.scrollY;
			const scrollDelta = Math.abs(currentScrollY - lastScrollY.current);

			// Only dismiss keyboard if user touch-scrolled more than threshold
			if (scrollDelta > scrollThreshold && isUserTouchScrolling.current) {
				const activeElement = document.activeElement as HTMLElement;

				// Check if an input, textarea, or contenteditable element is focused
				if (
					activeElement &&
					(activeElement.tagName === 'INPUT' ||
						activeElement.tagName === 'TEXTAREA' ||
						activeElement.getAttribute('contenteditable') ===
							'true')
				) {
					// Blur the active element to hide the keyboard
					activeElement.blur();
				}
			}

			lastScrollY.current = currentScrollY;

			// Clear existing timeout
			if (scrollTimeout.current) {
				clearTimeout(scrollTimeout.current);
			}

			// Reset user scrolling flag after scroll ends
			scrollTimeout.current = setTimeout(() => {
				isUserTouchScrolling.current = false;
			}, 150);
		};

		// Add event listeners with passive flag for better scroll performance
		window.addEventListener('touchstart', handleTouchStart, {
			passive: true,
		});
		window.addEventListener('touchmove', handleTouchMove, {
			passive: true,
		});
		window.addEventListener('touchend', handleTouchEnd, { passive: true });
		window.addEventListener('scroll', handleScroll, { passive: true });

		return () => {
			window.removeEventListener('touchstart', handleTouchStart);
			window.removeEventListener('touchmove', handleTouchMove);
			window.removeEventListener('touchend', handleTouchEnd);
			window.removeEventListener('scroll', handleScroll);
			if (scrollTimeout.current) {
				clearTimeout(scrollTimeout.current);
			}
		};
	}, []);
}

export function KeyboardDismissProvider({
	children,
}: KeyboardDismissProviderProps) {
	// Initialize the keyboard dismiss behavior globally
	useKeyboardDismiss();

	return <>{children}</>;
}
